import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BudgetIcon, LocationIcon } from "./icons";

const THRESHOLD = 0.5;
const GRADIENT_THRESHOLD = 0.25;

export default function Card({
  index,
  img,
  title,
  text,
  examples,
  cta,
  category,
  postcode,
  budget,
  className,
  handleResponse,
  backgroundCard = false,
  currentResponse = null,
  setGradientOpacity,
}) {
  const [startX, setStartX] = useState(null);
  const [startY, setStartY] = useState(null);
  const [currentX, setCurrentX] = useState(null);
  const [currentY, setCurrentY] = useState(null);
  const [bodyWidth, setBodyWidth] = useState(450);
  const deltaX = startX !== null && currentX !== null ? currentX - startX : 0;
  const deltaY = startY !== null && currentY !== null ? currentY - startY : 0;
  const ratio = parseFloat((deltaX / (bodyWidth / 2)).toFixed(4));
  const ratioRef = useRef(ratio);
  ratioRef.current = ratio;

  useEffect(() => {
    setGradientOpacity(
      deltaX < 0
        ? { left: Math.abs(ratio / GRADIENT_THRESHOLD), right: 0 }
        : { left: 0, right: ratio / GRADIENT_THRESHOLD },
    );
  }, [deltaX, ratio]);

  useEffect(() => {
    const { width } = document.body.getBoundingClientRect();
    setBodyWidth(width);
  }, []);

  const endRef = useRef();
  const draggingRef = useRef(false);

  useEffect(() => {
    if (backgroundCard) return;

    const handleLeave = (e) => endRef.current(e);
    document.body.addEventListener("mouseleave", handleLeave);

    return () => document.body.removeEventListener("mouseleave", handleLeave);
  }, []);

  const handleStart = (event) => {
    if (backgroundCard) return;
    if (event.type === "touchstart" && startsInScrollableText(event.target)) {
      return;
    }

    const { clientX, clientY } = getClientCoordinates(event);
    setStartX(clientX);
    setCurrentX(clientX);
    setStartY(clientY);
    setCurrentY(clientY);

    draggingRef.current = true;
    document.body.addEventListener("mousemove", handleMove);
    document.body.addEventListener("touchmove", handleMove);
    document.body.addEventListener("mouseup", stableEnd);
  };

  const handleMove = useCallback((event) => {
    const coords = getClientCoordinates(event);
    if (!coords) return;

    setCurrentX(coords.clientX);
    setCurrentY(coords.clientY);
  }, []);

  const handleEnd = (event) => {
    setGradientOpacity({ left: 0, right: 0 });
    if (backgroundCard) return;
    if (!draggingRef.current) return;
    draggingRef.current = false;

    if (ratioRef.current > THRESHOLD) {
      handleResponse(event, true);
    } else if (ratioRef.current < -THRESHOLD) {
      handleResponse(event, false);
    } else {
      setStartX(null);
      setStartY(null);
      setCurrentX(null);
      setCurrentY(null);
    }

    document.body.removeEventListener("touchmove", handleMove);
    document.body.removeEventListener("mousemove", handleMove);
    document.body.removeEventListener("mouseup", stableEnd);
  };
  endRef.current = handleEnd;
  const stableEnd = useCallback((event) => endRef.current(event), []);

  const startsInScrollableText = (target) => {
    const el = target?.closest?.(".card__description");
    return !!el && el.scrollHeight > el.clientHeight;
  };

  const getClientCoordinates = (event) => {
    if (["touchstart", "touchmove"].includes(event.type)) {
      if (event.changedTouches.length < 1) return;

      const touch = event.changedTouches[0];
      return { clientX: touch.clientX, clientY: touch.clientY };
    } else {
      const { clientX, clientY } = event;
      return { clientX, clientY };
    }
  };

  const styles = useMemo(() => {
    if (backgroundCard) return {};

    if (currentResponse !== null) {
      const newDeltaX = (deltaX + (currentResponse ? 180 : -180)).toFixed(4);
      const newRatio = (newDeltaX / (bodyWidth / 2)).toFixed(4);

      return {
        transform: `translate(${newDeltaX}px, ${deltaY}px) rotate(${-15 * newRatio}deg)`,
        "--overlay-opacity": "100%",
      };
    }

    return {
      transform: `translate(${deltaX}px, ${deltaY}px) rotate(${-15 * ratio}deg)`,
      "--overlay-opacity": `${Math.abs(ratio) / THRESHOLD}`,
    };
  }, [deltaX, deltaY, bodyWidth, currentResponse, backgroundCard]);

  const hasMediaPills = category || postcode || budget || index;

  return (
    <div
      className={clsx(
        "card",
        className,
        startX !== null && "card--moving",
        deltaX < 0 && "card--reject",
        currentResponse === false && "card--reject",
        backgroundCard && "card--background",
      )}
      onMouseDown={handleStart}
      onMouseUp={handleEnd}
      onTouchStart={handleStart}
      onTouchEnd={handleEnd}
      onTouchCancel={handleEnd}
      style={styles}
      aria-hidden={backgroundCard}
    >
      {img && (
        <div className="card__media">
          <img className="card__image" src={img} alt="" draggable={false} />
          {hasMediaPills && (
            <>
              {index && (
                <span className="card__pill card__pill--progress">{index}</span>
              )}
              {category && (
                <span className="card__pill card__pill--category">
                  {category}
                </span>
              )}
              {(budget || postcode) && (
                <div className="card__pill-row">
                  {budget && (
                    <span className="card__pill">
                      <BudgetIcon className="card__pill-icon" />
                      {budget}
                    </span>
                  )}
                  {postcode && (
                    <span className="card__pill">
                      <LocationIcon className="card__pill-icon" />
                      {postcode}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
      <h1 className="card__title">{title}</h1>
      <p
        className="card__text card__description"
        onWheel={(e) => e.stopPropagation()}
      >
        {text}
      </p>
      {cta && <p className="card__text card__text--primary">{cta}</p>}
      {examples && <p className="card__text card__text--grey">{examples}</p>}
    </div>
  );
}
